import React from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { Search as SearchIcon } from '@mui/icons-material';
import { DirectionsCar as CarIcon } from '@mui/icons-material';
import { Person as PersonIcon } from '@mui/icons-material';
import { Build as BuildIcon } from '@mui/icons-material';
import { Assignment as OrderIcon } from '@mui/icons-material';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

import Button from '@mui/material/Button';
import { StateManager } from '../utilities/stateManager';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import parse from 'html-react-parser';
import SizedImg from './SizedImg';

export default function ResultsList(props) {
    const classes = useStyles();
    const { results, selected, onSelect } = props;

    // Run a row's action, then let the owner react to the selection (the header
    // search bar uses this to clear itself once something has been picked).
    const handleSelect = (item, event) => {
      if (typeof item.action === 'function') item.action(event);
      if (typeof onSelect === 'function') onSelect(item, event);
    };

    const [open, setOpen] = React.useState(results.length > 0);
    StateManager.openSearch = setOpen;

    React.useEffect(() => {
      function updateResults() {
          const isOpen = results.length > 0;
          setOpen(isOpen)
      }
      updateResults();
  }, [results]);

  // Handle click outside to close results
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && !event.target.closest('[data-search-results]')) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  // Group results by type
  const groupedResults = React.useMemo(() => {
    const groups = {};
    results.forEach(item => {
      // Skip grouping for "view all" items - they'll be shown separately
      if (item.isViewAll) return;
      
      const type = item.type || 'Other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(item);
    });
    return groups;
  }, [results]);

  // Separate view all items
  const viewAllItems = results.filter(item => item.isViewAll);
  
  // Get icon for result type
  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'car':
      case 'cars':
        return <CarIcon className={classes.typeIcon} />;
      case 'customer':
      case 'customers':
        return <PersonIcon className={classes.typeIcon} />;
      case 'service':
      case 'repair':
        return <BuildIcon className={classes.typeIcon} />;
      case 'order':
      case 'orders':
        return <OrderIcon className={classes.typeIcon} />;
      default:
        return <SearchIcon className={classes.typeIcon} />;
    }
  };

  // Get display name for result type
  const getTypeDisplayName = (type) => {
    switch (type?.toLowerCase()) {
      case 'car':
      case 'cars':
        return 'Cars';
      case 'customer':
      case 'customers':
        return 'Customers';
      case 'service':
      case 'repair':
        return 'Service Orders';
      case 'order':
      case 'orders':
        return 'Orders';
      default:
        return type || 'Other';
    }
  };
  
    return (
      <>
        {
          open && 
          <div 
            className={classes.resultsContainer}
            data-search-results
          >
            <div className={classes.resultsContent}>
              {/* Show view all items first without section header */}
              {viewAllItems.length > 0 && (
                <List className={classes.resultsList}>
                  {viewAllItems.map((item, i) => {
                    const globalIndex = results.findIndex(r => r === item);
                    return (
                      <div 
                        key={`view-all-${i}`}
                        className={classes.itemContainer}
                        style={{
                          backgroundColor: selected === globalIndex ? "#f8f9fa" : "white",
                          borderLeft: "4px solid #1976d2"
                        }}
                      >
                        <ListItem 
                          dense 
                          button 
                          onClick={(e) => handleSelect(item, e)}
                          className={classes.listItem}
                        >
                          <ListItemIcon className={classes.itemIcon}>
                            <SearchIcon className={classes.searchIcon} />
                          </ListItemIcon>
                          <ListItemText 
                            className={classes.itemText} 
                            id={item.objectID} 
                            primary={cleanTags(item.label).map(parse)}
                          />
                          <ListItemSecondaryAction>
                            <NavigateNextIcon 
                              className={classes.navigateIcon}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      </div>
                    );
                  })}
                </List>
              )}

              {/* Show grouped results with section headers */}
              {Object.entries(groupedResults).map(([type, typeResults], groupIndex) => (
                <div key={type} className={classes.resultSection}>
                  {/* Section Header */}
                  <Box className={classes.sectionHeader}>
                    {getTypeIcon(type)}
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {getTypeDisplayName(type)}
                    </Typography>
                    <Typography variant="body2" className={classes.resultCount}>
                      {typeResults.length} result{typeResults.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  
                  {/* Section Divider */}
                  <Divider className={classes.sectionDivider} />
                  
                  {/* Results List */}
                  <List className={classes.resultsList}>
                    {typeResults.map((item, i) => {
                      const globalIndex = results.findIndex(r => r === item);
                      return (
                        <div 
                          key={`${item.type || 'item'}-${item.objectID || i}`}
                          className={classes.itemContainer}
                          style={{
                            backgroundColor: selected === globalIndex ? "#f8f9fa" : "white",
                            borderLeft: item.isViewAll ? "4px solid #1976d2" : "none"
                          }}
                        >
                          <ListItem
                            dense
                            button
                            onClick={(e) => handleSelect(item, e)}
                            className={classes.listItem}
                          >
                            {
                              props.removeIcon || (
                                <ListItemIcon className={classes.itemIcon}>
                                  <SizedImg
                                    photoWidth={400}
                                    className={classes.thumbnail}
                                    src={item.thumbnail || deafultImage}
                                    alt=""
                                  />
                                </ListItemIcon>
                              )
                            }
                            <ListItemText 
                              className={classes.itemText} 
                              id={item.objectID} 
                              primary={cleanTags(item.label).map(parse)}
                            />
                            <ListItemSecondaryAction>
                              <NavigateNextIcon 
                                className={classes.navigateIcon}
                              />
                            </ListItemSecondaryAction>
                          </ListItem>
                        </div>
                      );
                    })}
                  </List>
                  
                  {/* Add spacing between sections */}
                  {groupIndex < Object.keys(groupedResults).length - 1 && (
                    <Box className={classes.sectionSpacing} />
                  )}
                </div>
              ))}
            </div>
          </div>
        }
      </>
    );
  };

const useStyles = makeStyles((theme) => ({
  resultsContainer: {
    position: 'absolute',
    top: '100%',
    left: '12.5%', // Center the 75% width container
    right: '12.5%',
    zIndex: 1300,
    backgroundColor: 'white',
    borderRadius: '0 0 12px 12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderTop: 'none',
    maxHeight: '400px', // Prevent excessive height on desktop
    // Mobile-specific improvements
    [theme.breakpoints.down('sm')]: {
      left: 0,
      right: 0,
      maxHeight: '60vh', // Use viewport height on mobile
      borderRadius: '12px',
      border: '1px solid #e0e0e0',
      borderTop: '1px solid #e0e0e0',
      // Improve scrolling on mobile
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain',
    },
  },
  resultsContent: {
    padding: '16px 0',
  },
  resultSection: {
    marginBottom: '8px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px 12px 24px',
    gap: '12px',
  },
  typeIcon: {
    color: '#1976d2',
    fontSize: '24px',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#1a1a1a',
    flex: 1,
  },
  resultCount: {
    color: '#666',
    fontSize: '14px',
  },
  sectionDivider: {
    margin: '0 24px 16px 24px',
    backgroundColor: '#e0e0e0',
  },
  sectionSpacing: {
    height: '24px',
  },
  resultsList: {
    padding: 0,
  },
  itemContainer: {
    margin: '0 24px 8px 24px',
    borderRadius: '8px',
    border: '1px solid #f0f0f0',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#d0d0d0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
  },
  listItem: {
    padding: '16px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    // Mobile-specific improvements
    [theme.breakpoints.down('sm')]: {
      padding: '20px', // Larger touch targets on mobile
      minHeight: '56px', // Ensure minimum touch target size
      '&:active': {
        backgroundColor: '#e3f2fd', // Visual feedback on touch
      },
    },
  },
  itemIcon: {
    minWidth: '48px',
  },
  searchIcon: {
    fontSize: '24px',
    color: '#1976d2',
  },
  thumbnail: {
    width: '48px',
    height: '48px',
    borderRadius: '6px',
    objectFit: 'cover',
  },
  itemText: {
    color: '#1a1a1a',
    marginLeft: '12px',
    '& .MuiListItemText-primary': {
      fontSize: '15px',
      lineHeight: '1.4',
    },
  },
  navigateIcon: {
    color: '#666',
    opacity: 0.7,
  },
}));

const deafultImage = 	"https://admin.dealeraccelerate.com/assets/image_processing_low_res-b9fff6cfb5c67f8ba64b79debc32fdf1.png";

const cleanTags = (text) => {
  text = text.replace(/em>/g, "strong>");
  const tags = text.match(/<\D+>/g) || [];
  let parts = text.split(/<\D+>/g);

  tags.forEach((tag, i) => {
    if(i%2 == 0) parts[i+1] = tag+parts[i+1];
    else parts[i] = parts[i]+tag;
  });
  return parts;
};